import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';

/* ===========================================================================
 * Sidebar — componente único de menu lateral, usado por ClientLayout E
 * AdminLayout. Evita duplicar CSS/estrutura (e bugs, como o de z-index do
 * drawer mobile que precisaria ser corrigido 2x se os layouts continuassem
 * separados).
 *
 * Props:
 *  - brandIcon, brandText: logo da sidebar
 *  - navItems: [{ to, icon, iconLib?, label, end?, children?: [...] }]
 *    (itens com "children" viram um grupo expansível, ex: "Integrações")
 *  - topbarExtra: conteúdo à direita da topbar mobile (ex: chip de créditos)
 *  - footerExtra: conteúdo extra no rodapé, ANTES de tema/sair (ex: chip de
 *    créditos no desktop, ou "voltar ao painel do cliente")
 *  - onLogout: handler do botão "Sair"
 *  - topbarBrand: texto da marca na topbar mobile (default = brandText)
 * ========================================================================= */

const Sidebar = ({
  brandIcon, brandText, navItems, topbarExtra, footerExtra, footerButtons, onLogout, topbarBrand,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  const closeMobile = () => setMobileOpen(false);
  const toggleGroup = (label) => setOpenGroups((g) => ({ ...g, [label]: !g[label] }));

  return (
    <div className="sb-root">
      <style>{styles}</style>

      <header className="sb-topbar-mobile">
        <button className="sb-burger" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
          <i className="fas fa-bars"></i>
        </button>
        <span className="sb-topbar-brand">{topbarBrand || brandText}</span>
        <div className="sb-topbar-right">{topbarExtra}</div>
      </header>

      {mobileOpen && <div className="sb-overlay" onClick={closeMobile}></div>}

      <aside className={`sb-sidebar ${mobileOpen ? 'is-open' : ''}`}>
        <div className="sb-sidebar-head">
          <span className="sb-brand"><i className={`fas ${brandIcon}`}></i> {brandText}</span>
          <button className="sb-close-mobile" onClick={closeMobile} aria-label="Fechar menu">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {footerExtra && <div className="sb-head-extra">{footerExtra}</div>}

        <nav className="sb-nav">
          {navItems.map((item) => {
            if (item.children) {
              const isOpen = openGroups[item.label] !== false; // default aberto
              return (
                <React.Fragment key={item.label}>
                  <button className="sb-nav-group-toggle" onClick={() => toggleGroup(item.label)}>
                    <i className={`${item.iconLib || 'fas'} ${item.icon}`}></i> {item.label}
                    <i className={`fas fa-chevron-down sb-chev ${isOpen ? 'is-open' : ''}`}></i>
                  </button>
                  {isOpen && (
                    <div className="sb-nav-subgroup">
                      {item.children.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          className={({ isActive }) => `sb-nav-item sb-nav-item-sub ${isActive ? 'is-active' : ''}`}
                          onClick={closeMobile}
                        >
                          <i className={`${sub.iconLib || 'fas'} ${sub.icon}`}></i> {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `sb-nav-item ${item.variant || ''} ${isActive ? 'is-active' : ''}`}
                onClick={closeMobile}
              >
                <i className={`${item.iconLib || 'fas'} ${item.icon}`}></i> {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="sb-sidebar-footer">
          <ThemeToggle className="sb-footer-btn" as="menu-item" />
          {(footerButtons || []).map((btn) => (
            <button key={btn.label} className="sb-footer-btn" onClick={btn.onClick}>
              <i className={`fas ${btn.icon}`}></i> {btn.label}
            </button>
          ))}
          <button className="sb-footer-btn sb-footer-logout" onClick={onLogout}>
            <i className="fas fa-sign-out-alt"></i> Sair
          </button>
        </div>
      </aside>

      <main className="sb-content">
        <Outlet />
      </main>
    </div>
  );
};

const styles = `
  .sb-root { display: flex; min-height: 100vh; background: #0f0f1a; overflow-x: hidden; }

  .sb-sidebar {
    width: 260px; flex-shrink: 0; background: linear-gradient(180deg, #1a1a2e, #16213e);
    display: flex; flex-direction: column; padding: 20px 16px;
    position: sticky; top: 0; height: 100vh; overflow-y: auto;
    border-right: 1px solid rgba(255,255,255,0.06);
  }
  .sb-sidebar-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
  .sb-brand { color: #fff; font-family: 'Lexend', sans-serif; font-weight: 700; font-size: 17px; display: flex; align-items: center; gap: 8px; }
  .sb-brand i { color: #ff9900; }
  .sb-close-mobile { display: none; background: none; border: none; color: #fff; font-size: 18px; cursor: pointer; }
  .sb-head-extra { margin-bottom: 14px; }

  .sb-nav { display: flex; flex-direction: column; gap: 4px; flex: 1; }
  .sb-nav-item, .sb-nav-group-toggle {
    display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 10px;
    color: rgba(255,255,255,0.75); text-decoration: none; font-family: 'Lexend', sans-serif;
    font-size: 14px; border: none; background: none; cursor: pointer; width: 100%; text-align: left;
    white-space: nowrap;
  }
  .sb-nav-item:hover, .sb-nav-group-toggle:hover { background: rgba(255,255,255,0.06); color: #fff; }
  .sb-nav-item.is-active, .sb-nav-item-sub.is-active {
    background: #ff9900 !important; color: #1a1a2e !important; font-weight: 600;
  }
  .sb-nav-item.is-active i, .sb-nav-item-sub.is-active i { color: #1a1a2e !important; }
  .sb-nav-item-sub {
    padding-left: 34px; font-size: 13.5px; overflow: hidden; text-overflow: ellipsis;
  }
  /* :not(.is-active) garante que isso NUNCA compete com a cor do estado ativo
     (fundo laranja + texto escuro), independente de prioridade/especificidade. */
  .sb-nav-item-sub:not(.is-active) { color: rgba(255,255,255,0.90) !important; }
  .sb-nav-item-sub:not(.is-active) i { color: rgba(255,255,255,0.90) !important; }
  .sb-nav-item-sub:hover:not(.is-active) { color: #fff !important; }
  .sb-nav-item-sub:hover:not(.is-active) i { color: #fff !important; }
  .sb-nav-subgroup { display: flex; flex-direction: column; gap: 2px; }
  .sb-chev { margin-left: auto; font-size: 11px; transition: transform 0.2s; }
  .sb-chev.is-open { transform: rotate(180deg); }
  .sb-nav-item.variant-top { margin-top: 10px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 14px; }

  .sb-sidebar-footer { display: flex; flex-direction: column; gap: 4px; margin-top: 14px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 14px; }
  .sb-footer-btn {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 10px;
    background: none; border: none; color: rgba(255,255,255,0.7); font-family: 'Lexend', sans-serif;
    font-size: 13px; cursor: pointer; text-align: left; width: 100%;
  }
  .sb-footer-btn:hover { background: rgba(255,255,255,0.06); color: #fff; }
  .sb-footer-logout:hover { background: rgba(239,68,68,0.15); color: #ef4444; }

  .sb-content { flex: 1; min-width: 0; min-height: 100vh; overflow-x: hidden; }

  .sb-topbar-mobile { display: none; }
  .sb-overlay { display: none; }

  @media (max-width: 900px) {
    .sb-root { flex-direction: column; }
    .sb-topbar-mobile {
      display: flex; align-items: center; justify-content: space-between; gap: 10px;
      padding: 12px 16px; background: linear-gradient(135deg, #1a1a2e, #16213e);
      position: sticky; top: 0;
      /* isolation + z-index baixo: o drawer/overlay (muito mais alto) precisam
         sempre vencer essa topbar, mesmo com bugs conhecidos de "sticky" no
         Safari/iOS que ignoram z-index entre contextos diferentes. */
      z-index: 10; isolation: isolate;
    }
    .sb-burger { background: none; border: none; color: #fff; font-size: 20px; }
    .sb-topbar-brand { color: #fff; font-family: 'Lexend', sans-serif; font-weight: 700; }
    .sb-topbar-right { display: flex; align-items: center; gap: 8px; }

    .sb-overlay {
      display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 9998;
    }
    .sb-sidebar {
      position: fixed; top: 0; left: 0; height: 100vh; z-index: 9999;
      transform: translateX(-100%); transition: transform 0.25s ease; width: 78vw; max-width: 300px;
    }
    .sb-sidebar.is-open { transform: translateX(0); box-shadow: 8px 0 30px rgba(0,0,0,0.4); }
    .sb-close-mobile { display: block; }
  }

  /* MODAL DE SENHA (compartilhado pelo ClientLayout — ChangePasswordModal.js)
     Sempre com essa paleta viva, independente do tema claro/escuro da página
     (igual a sidebar, que também é sempre escura) — evita ficar "apagado"
     quando o usuário troca pro modo escuro. */
  .dash-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex;
    align-items: center; justify-content: center; z-index: 999; padding: 16px;
  }
  .dash-modal {
    background: linear-gradient(160deg, #23234a 0%, #1a1a2e 100%);
    border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 24px;
    width: 100%; max-width: 400px; color: #fff; box-shadow: 0 24px 60px rgba(0,0,0,0.5);
  }
  .dash-modal-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .dash-modal-head h3 { font-family: 'Lexend', sans-serif; font-size: 18px; font-weight: 700; }
  .dash-modal-close {
    width: 28px; height: 28px; border-radius: 8px; background: rgba(255,255,255,0.08);
    border: none; color: #fff; font-size: 14px; cursor: pointer;
  }
  .dash-modal-close:hover { background: rgba(255,255,255,0.15); }
  .dash-form-group { margin-bottom: 14px; }
  .dash-form-label { display: block; font-size: 12px; margin-bottom: 6px; color: rgba(255,255,255,0.75); font-weight: 500; }
  .dash-form-input {
    width: 100%; padding: 11px 13px; border-radius: 9px; border: 1.5px solid rgba(255,255,255,0.18);
    background: rgba(255,255,255,0.07); color: #fff; font-size: 14px; font-family: 'Lexend', sans-serif;
    transition: all 0.2s;
  }
  .dash-form-input:focus { outline: none; border-color: #ff9900; box-shadow: 0 0 0 3px rgba(255,153,0,0.20); background: rgba(255,255,255,0.1); }
  .dash-modal-submit {
    width: 100%; padding: 13px; border-radius: 10px; border: none; background: linear-gradient(135deg, #ffaa22, #ff9900);
    color: #1a1a2e; font-weight: 700; font-size: 14.5px; cursor: pointer; margin-top: 6px; font-family: 'Lexend', sans-serif;
    box-shadow: 0 4px 16px rgba(255,153,0,0.35); transition: all 0.2s;
  }
  .dash-modal-submit:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(255,153,0,0.45); }
  .dash-modal-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  .dash-alert { padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 13px; }
  .dash-alert.error { background: rgba(239,68,68,0.18); color: #fca5a5; border: 1px solid rgba(239,68,68,0.3); }
  .dash-alert.success { background: rgba(34,197,94,0.18); color: #86efac; border: 1px solid rgba(34,197,94,0.3); }

  /* Chip de créditos (reutilizado pelo ClientLayout como footerExtra) */
  .sb-credits-chip {
    display: flex; align-items: center; gap: 10px; background: rgba(255,153,0,0.12);
    border: 1px solid rgba(255,153,0,0.3); border-radius: 12px; padding: 10px 14px;
    color: #ff9900;
  }
  .sb-credits-chip.low { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.3); color: #ef4444; }
  .sb-credits-chip.empty { background: rgba(239,68,68,0.18); border-color: rgba(239,68,68,0.4); color: #ef4444; }
  .sb-credits-chip-label { display: block; font-size: 10.5px; text-transform: uppercase; opacity: 0.8; }
  .sb-credits-chip-val { display: block; font-size: 18px; font-weight: 800; }
  .sb-topbar-credits { color: #ff9900; font-size: 13px; font-weight: 700; display: flex; gap: 6px; align-items: center; background:none; border:none; cursor:pointer; }
  .sb-topbar-credits.empty { color: #ef4444; }
`;

export default Sidebar;
